/**
 * AdjusterAnalysisTab — Adjuster lens analysis for the Analysis stage
 *
 * Phase 4.4 — redesign-p4-analatt-44b
 * Wires to existing /api/v1/adjuster/analysis/{reportId} endpoint
 * Uses FinancialValue, MetricCard, StatusBadge primitives per design spec
 */

import React, { useEffect, useState } from 'react';
import { Shield, AlertTriangle, FileCheck, TrendingDown, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { FinancialValue } from '@/components/shared/FinancialValue';
import { MetricCard } from '@/components/shared/MetricCard';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { adjusterAnalyzerApi, AdjusterAnalysisResult } from '@/services/adjusterAnalyzerApi';
import { BRAND_NAME } from '@/config/brand';

interface AdjusterAnalysisTabProps {
  reportId: string;
}

export function AdjusterAnalysisTab({ reportId }: AdjusterAnalysisTabProps) {
  const [analysis, setAnalysis] = useState<AdjusterAnalysisResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    gaps: true,
    arguments: true,
    causation: false,
  });

  const fetchAnalysis = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adjusterAnalyzerApi.getAnalysis(reportId);
      setAnalysis(data);
    } catch (err: any) {
      if (err.message?.includes('not found') || err.message?.includes('Run analysis first')) {
        setAnalysis(null);
      } else {
        setError(err?.message || 'Failed to load adjuster analysis');
      }
    } finally {
      setLoading(false);
    }
  };

  const runAnalysis = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const result = await adjusterAnalyzerApi.runAnalysis(reportId, forceRefresh);
      setAnalysis(result.result);
    } catch (err: any) {
      setError(err?.message || 'Failed to run adjuster analysis');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalysis();
  }, [reportId]);

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const getRiskVariant = (risk?: string): Parameters<typeof StatusBadge>[0]['variant'] => {
    switch (risk) {
      case 'LOW':
        return 'done';
      case 'MEDIUM':
        return 'warning';
      case 'HIGH':
        return 'attention';
      default:
        return 'neutral';
    }
  };

  if (loading && !analysis) {
    return (
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-[var(--color-brand)]" />
          <span className="ml-3 text-sm text-[var(--color-text-secondary)]">
            Loading adjuster analysis…
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6">
        <div className="rounded-lg bg-[rgba(255,102,0,0.08)] p-4">
          <p className="text-sm text-[var(--color-text-primary)]">{error}</p>
          <button
            onClick={() => runAnalysis(true)}
            className="mt-3 rounded-md bg-[var(--color-brand)] px-4 py-2 text-sm font-medium text-[var(--color-on-brand)] hover:bg-[var(--color-brand-hover)]"
          >
            Retry Analysis
          </button>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6">
        <div className="py-12 text-center">
          <Shield className="mx-auto h-8 w-8 text-[var(--color-text-ghost)]" />
          <p className="mt-3 text-sm text-[var(--color-text-muted)]">
            No adjuster analysis available yet.
          </p>
          <button
            onClick={() => runAnalysis(false)}
            disabled={loading}
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-[var(--color-brand)] px-4 py-2 text-sm font-medium text-[var(--color-on-brand)] hover:bg-[var(--color-brand-hover)] disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Run adjuster analysis
          </button>
        </div>
      </div>
    );
  }

  const { executive_summary, position, documentation_analysis, causation_analysis, risk_assessment, carrier_bridge, reduction_arguments } = analysis;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-[var(--color-needs-human-tint)] p-2">
            <Shield className="h-5 w-5 text-[var(--color-brand)]" />
          </div>
          <div>
            <h2
              className="text-lg font-semibold text-[var(--color-text-primary)]"
              style={{ fontFamily: 'DM Serif Display, Georgia, serif' }}
            >
              Adjuster Analysis
            </h2>
            <p className="text-xs text-[var(--color-text-muted)]">
              Insurance carrier position assessment
            </p>
          </div>
        </div>
        <button
          onClick={() => runAnalysis(true)}
          disabled={loading}
          className="flex items-center gap-2 rounded-md border border-[var(--color-brand)] px-3 py-1.5 text-sm font-medium text-[var(--color-brand)] hover:bg-[var(--color-brand)] hover:text-[var(--color-on-brand)] disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Executive Summary */}
      {executive_summary && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-5">
          <h3
            className="mb-4 text-base font-semibold text-[var(--color-text-primary)]"
            style={{ fontFamily: 'DM Serif Display, Georgia, serif' }}
          >
            Executive Summary
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <MetricCard
              label="Attorney Claimed"
              value={<FinancialValue value={executive_summary.attorney_claimed_total} />}
            />
            <MetricCard
              label="Supported"
              value={<FinancialValue value={executive_summary.supported_total} />}
              badge={{ text: 'Carrier View', variant: 'done' }}
            />
            <MetricCard
              label="Questioned"
              value={<FinancialValue value={executive_summary.questioned_total} />}
              badge={{ text: 'At Risk', variant: 'warning' }}
              accent
            />
            <MetricCard
              label="Denied"
              value={<FinancialValue value={executive_summary.denied_total} />}
              badge={{ text: 'Denied', variant: 'blocked' }}
            />
          </div>
        </div>
      )}

      {/* Risk Assessment */}
      {risk_assessment && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-5">
          <h3 className="mb-4 text-sm font-medium text-[var(--color-text-primary)]">Risk Assessment</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="flex items-center justify-between rounded-lg bg-[var(--color-bg-elevated)] p-3">
              <span className="text-sm text-[var(--color-text-secondary)]">Underpayment Risk</span>
              <StatusBadge text={risk_assessment.underpayment_risk} variant={getRiskVariant(risk_assessment.underpayment_risk)} />
            </div>
            <div className="flex items-center justify-between rounded-lg bg-[var(--color-bg-elevated)] p-3">
              <span className="text-sm text-[var(--color-text-secondary)]">Litigation Risk</span>
              <StatusBadge text={risk_assessment.litigation_risk} variant={getRiskVariant(risk_assessment.litigation_risk)} />
            </div>
            <div className="flex items-center justify-between rounded-lg bg-[var(--color-bg-elevated)] p-3">
              <span className="text-sm text-[var(--color-text-secondary)]">Bad Faith Risk</span>
              <StatusBadge text={risk_assessment.bad_faith_risk} variant={getRiskVariant(risk_assessment.bad_faith_risk)} />
            </div>
          </div>
          {risk_assessment.risk_notes && (
            <p className="mt-3 text-sm text-[var(--color-text-secondary)]">{risk_assessment.risk_notes}</p>
          )}
        </div>
      )}

      {/* Documentation Gaps */}
      {documentation_analysis?.gaps && documentation_analysis.gaps.length > 0 && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
          <button
            onClick={() => toggleSection('gaps')}
            className="flex w-full items-center justify-between p-4 hover:bg-[var(--color-bg-hover)]"
          >
            <div className="flex items-center gap-3">
              <FileCheck className="h-5 w-5 text-[var(--color-brand)]" />
              <span className="font-medium text-[var(--color-text-primary)]">
                Documentation Gaps ({documentation_analysis.gaps.length})
              </span>
              <span className="text-xs text-[var(--color-text-muted)]">
                Completeness: {Math.round(documentation_analysis.overall_completeness * 100)}%
              </span>
            </div>
            {expandedSections.gaps ? (
              <ChevronUp className="h-5 w-5 text-[var(--color-text-muted)]" />
            ) : (
              <ChevronDown className="h-5 w-5 text-[var(--color-text-muted)]" />
            )}
          </button>

          {expandedSections.gaps && (
            <div className="space-y-2 border-t border-[var(--color-border-sub)] p-4">
              {documentation_analysis.gaps.map((gap, idx) => (
                <div key={idx} className="rounded-lg border border-[var(--color-border-sub)] bg-[var(--color-bg-elevated)] p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="font-medium text-[var(--color-text-primary)]">{gap.description}</span>
                    <StatusBadge
                      text={gap.category}
                      variant={gap.category === 'CRITICAL' ? 'attention' : gap.category === 'MAJOR' ? 'warning' : 'neutral'}
                      size="sm"
                    />
                  </div>
                  <p className="text-xs text-[var(--color-text-muted)]">
                    Remediation: {gap.remediation}
                  </p>
                  {gap.impact_estimate > 0 && (
                    <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
                      Impact: <FinancialValue value={gap.impact_estimate} />
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Reduction Arguments */}
      {reduction_arguments && reduction_arguments.length > 0 && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
          <button
            onClick={() => toggleSection('arguments')}
            className="flex w-full items-center justify-between p-4 hover:bg-[var(--color-bg-hover)]"
          >
            <div className="flex items-center gap-3">
              <TrendingDown className="h-5 w-5 text-[var(--color-brand)]" />
              <span className="font-medium text-[var(--color-text-primary)]">
                Reduction Arguments ({reduction_arguments.length})
              </span>
            </div>
            {expandedSections.arguments ? (
              <ChevronUp className="h-5 w-5 text-[var(--color-text-muted)]" />
            ) : (
              <ChevronDown className="h-5 w-5 text-[var(--color-text-muted)]" />
            )}
          </button>

          {expandedSections.arguments && (
            <div className="space-y-2 border-t border-[var(--color-border-sub)] p-4">
              {reduction_arguments.map((arg, idx) => (
                <div key={idx} className="rounded-lg border border-[var(--color-border-sub)] bg-[var(--color-bg-elevated)] p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="font-medium text-[var(--color-text-primary)]">{arg.description}</span>
                    <StatusBadge
                      text={arg.strength}
                      variant={arg.strength === 'STRONG' ? 'attention' : arg.strength === 'MODERATE' ? 'warning' : 'neutral'}
                      size="sm"
                    />
                  </div>
                  <div className="flex items-center gap-4 text-xs text-[var(--color-text-muted)]">
                    <span>
                      Impact: <FinancialValue value={arg.dollar_impact} />
                    </span>
                    <span>Type: {arg.argument_type}</span>
                  </div>
                  {arg.counter_evidence_needed && (
                    <p className="mt-2 text-xs text-[var(--color-brand)]">
                      Counter evidence needed: {arg.counter_evidence_needed}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Carrier Position — Reconciliation Waterfall
          Continuation of the accountant/attorney reconciliation idiom: anchor on
          the engine (CP 00 30) actual-loss-sustained figure, then subtract the
          carrier's judgment-layer haircuts down to the supported position.
          Replaces the old per-category "claim items" table (a fabricated 70/15/15
          split). The two step layers are CARRIER JUDGMENT, not an ISO
          determination — see the note below the ledger. */}
      {carrier_bridge && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-5">
          <div className="mb-4 flex items-center gap-3">
            <TrendingDown className="h-5 w-5 text-[var(--color-brand)]" />
            <div>
              <h3
                className="text-base font-semibold text-[var(--color-text-primary)]"
                style={{ fontFamily: 'DM Serif Display, Georgia, serif' }}
              >
                Carrier Position — Reconciliation
              </h3>
              <p className="text-xs text-[var(--color-text-muted)]">
                Engine actual-loss-sustained figure, less the carrier's judgment-layer haircuts
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-[var(--color-border-sub)] bg-[var(--color-bg-elevated)]">
            {/* Anchor — the engine / CP 00 30 figure */}
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex flex-col pr-4">
                <span className="text-sm font-medium text-[var(--color-text-primary)]">
                  {carrier_bridge.anchor_label}
                </span>
                <span className="text-[11px] uppercase tracking-[0.08em] text-[var(--color-text-muted)]">
                  Deterministic engine · CP 00 30
                </span>
              </div>
              <FinancialValue
                value={carrier_bridge.anchor}
                cents
                size="lg"
                className="text-[color:var(--color-text-primary)]"
              />
            </div>

            {/* Carrier-judgment contra steps */}
            {carrier_bridge.steps.length > 0 && (
              <div className="border-t border-[var(--color-border-faint)]">
                <div className="px-4 pb-1 pt-2">
                  <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--color-text-muted)]">
                    {carrier_bridge.layer_label || 'Carrier judgment'}
                  </span>
                </div>
                {carrier_bridge.steps.map((step, idx) => (
                  <div key={idx} className="flex items-start justify-between px-4 py-2">
                    <div className="flex flex-col pr-4">
                      <span className="text-sm text-[var(--color-text-secondary)]">
                        Less — {step.label}
                      </span>
                      {step.basis && (
                        <span className="text-[11px] text-[var(--color-text-muted)]">{step.basis}</span>
                      )}
                    </div>
                    <FinancialValue value={step.amount} contra cents />
                  </div>
                ))}
              </div>
            )}

            {/* Ruled total — carrier-supported position */}
            <div className="flex items-center justify-between border-t-2 border-[var(--color-border)] px-4 py-3">
              <span className="text-sm font-semibold text-[var(--color-text-primary)]">
                {carrier_bridge.supported_label}
              </span>
              <FinancialValue
                value={carrier_bridge.supported}
                cents
                size="lg"
                className="text-[color:var(--color-text-primary)] font-semibold"
              />
            </div>
          </div>

          {/* Honest residual — carrier judgment, not carrier truth / not a clean engine figure */}
          {carrier_bridge.note && (
            <div className="mt-3 flex items-start gap-2 rounded-lg bg-[rgba(255,102,0,0.06)] p-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--color-brand)]" />
              <p className="text-xs leading-relaxed text-[var(--color-text-secondary)]">
                {carrier_bridge.note}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AdjusterAnalysisTab;
